import { assignablePeopleKeys, taskKeys } from "@/domains/Tasks/types";
import { agencyPeopleKeys } from "./agencyPeople";

/** people.name is String(255): backend NAME_MAX_LENGTH in person_entity.py. */
export const NAME_MAX_LENGTH = 255;

export const NAME_LOCKED_MESSAGE =
  "Nome non aggiornato: la persona è legata a un'altra agenzia.";

export const NAME_REQUIRED_MESSAGE = "Il nome non può essere vuoto.";

/** What a name field holds against the stored name. Clearing a name is not
 * supported (an empty name is what Google login fills), so it is ``blank``. */
export type NameEdit =
  | { kind: "unchanged" }
  | { kind: "blank" }
  | { kind: "rename"; name: string };

export function nameEdit(current: string | null, next: string): NameEdit {
  const trimmed = next.trim();
  if (trimmed === (current ?? "").trim()) return { kind: "unchanged" };
  if (!trimmed) return { kind: "blank" };
  return { kind: "rename", name: trimmed };
}

/**
 * True for the rename endpoint's 409 ``{ code: "NAME_LOCKED" }``. apiClient
 * rejects a 4xx whose body has no ``error`` string with the raw axios error,
 * so the status and body are still on ``err.response``.
 */
export function isNameLocked(err: unknown): boolean {
  if (typeof err !== "object" || err === null || !("response" in err)) return false;
  const { response } = err;
  if (typeof response !== "object" || response === null) return false;
  if (!("status" in response) || response.status !== 409) return false;
  if (!("data" in response)) return false;
  const { data } = response;
  return (
    typeof data === "object" &&
    data !== null &&
    "code" in data &&
    data.code === "NAME_LOCKED"
  );
}

export function renameErrorMessage(err: unknown, fallback: string): string {
  if (isNameLocked(err)) return NAME_LOCKED_MESSAGE;
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Prefixes of every cached query that shows a person's name: agency
 * people, each client's assignable people, and every task list (standalone,
 * idea, shot list). The client access list is component state, not a query. */
export const PERSON_NAME_QUERY_KEYS = [
  agencyPeopleKeys.all,
  assignablePeopleKeys.all,
  taskKeys.all,
] as const;
